import type { Account, Bank, User } from '../types';

/**
 * Which percentage applies to a collection.
 *
 * The specification asks for three layers -- the account's own rate, then the
 * bank's rate, then a 10% default. The hierarchy rules engine already existed
 * on top of that, so the full order is:
 *
 *   1. the account's own commissionPercent, when one is set
 *   2. a hierarchy rule or per-agent assignment that actually matched
 *   3. the bank's default rate
 *   4. the system default (10%)
 *
 * Layer 3 is the one that was missing. `Bank.defaultCommissionRate` was being
 * stored and shown in the banks screen, but nothing ever read it -- the engine
 * only matched rule rows, so a bank rate set in the UI did nothing at all and
 * every bank silently fell through to 10%.
 */

export interface RateResolution {
  percent: number;
  /** Where the number came from, for the audit trail and the UI. */
  source: 'account' | 'sarfaesi' | 'rule' | 'bank' | 'agent' | 'default';
  label: string;
}

/**
 * Values that look like a bank name but are not one. Imported sheets carry
 * placeholders such as "Partner Bank" where the real bank should be, and
 * looking those up found nothing -- so the rate silently fell through to the
 * 10% default however carefully the bank rate had been set.
 */
const NOT_A_BANK = new Set([
  '', 'n/a', 'na', 'null', '[null]', '-', 'none', 'all banks',
  'partner bank', 'bank', 'unknown', 'undefined',
]);

function usableBankName(name?: string): string {
  const v = (name || '').trim();
  return NOT_A_BANK.has(v.toLowerCase()) ? '' : v;
}

export function findBank(
  banks: Bank[],
  bankName?: string
): Bank | undefined {
  const target = usableBankName(bankName).toLowerCase();
  if (!target) return undefined;
  return (banks || []).find(
    (b) =>
      b.name?.trim().toLowerCase() === target ||
      b.code?.trim().toLowerCase() === target
  );
}

export function resolveCommissionPercent(params: {
  account?: Account | null;
  bankName?: string;
  banks: Bank[];
  /** The percentage the rules engine arrived at, if a rule actually matched. */
  matchedRulePercent?: number | null;
  matchedRuleName?: string | null;
  /** The person the collection is credited to. */
  agent?: User | null;
  /** The administrator's SARFAESI rate. */
  sarfaesiRate?: number;
  defaultRate: number;
}): RateResolution {
  const {
    account,
    bankName,
    banks,
    matchedRulePercent,
    matchedRuleName,
    agent,
    sarfaesiRate,
    defaultRate,
  } = params;

  const accountRate = account?.commissionPercent;
  if (typeof accountRate === 'number' && accountRate > 0) {
    return {
      percent: accountRate,
      source: 'account',
      label: `Account rate (${accountRate}%)`,
    };
  }

  // SARFAESI is a different kind of recovery and is paid at its own rate.
  // The agent decides which accounts are SARFAESI; the rate itself is an
  // administrator's setting, so classifying work can never become a way of
  // raising your own commission.
  if (account?.isSarfaesi) {
    const agentSarfaesi = agent?.sarfaesiCommissionPercent;
    if (typeof agentSarfaesi === 'number' && agentSarfaesi > 0) {
      return {
        percent: agentSarfaesi,
        source: 'sarfaesi',
        label: `SARFAESI rate for ${agent?.name || 'user'} (${agentSarfaesi}%)`,
      };
    }
    if (typeof sarfaesiRate === 'number' && sarfaesiRate > 0) {
      return {
        percent: sarfaesiRate,
        source: 'sarfaesi',
        label: `SARFAESI rate (${sarfaesiRate}%)`,
      };
    }
  }

  if (typeof matchedRulePercent === 'number' && matchedRulePercent > 0) {
    return {
      percent: matchedRulePercent,
      source: 'rule',
      label: matchedRuleName || `Hierarchy rule (${matchedRulePercent}%)`,
    };
  }

  const bank = findBank(banks, bankName || account?.bank);
  const bankRate = bank?.defaultCommissionRate;
  if (typeof bankRate === 'number' && bankRate > 0) {
    return {
      percent: bankRate,
      source: 'bank',
      label: `${bank?.name || 'Bank'} rate (${bankRate}%)`,
    };
  }

  // The rate set on the person when their login was created. Imported
  // accounts often carry a placeholder bank, so without this the carefully
  // set bank rate was never reachable and everything fell to 10%.
  const agentRate = agent?.commissionPercent;
  if (typeof agentRate === 'number' && agentRate > 0) {
    return {
      percent: agentRate,
      source: 'agent',
      label: `${agent?.name || 'User'} rate (${agentRate}%)`,
    };
  }

  // Finally the banks that person is attached to.
  const agentBankNames = [
    ...(Array.isArray(agent?.banks) ? agent!.banks : []),
    agent?.bank,
  ];
  for (const nm of agentBankNames) {
    const b = findBank(banks, nm);
    if (typeof b?.defaultCommissionRate === 'number' && b.defaultCommissionRate > 0) {
      return {
        percent: b.defaultCommissionRate,
        source: 'bank',
        label: `${b.name} rate (${b.defaultCommissionRate}%)`,
      };
    }
  }

  const fallback = typeof defaultRate === 'number' && defaultRate > 0 ? defaultRate : 10;
  return {
    percent: fallback,
    source: 'default',
    label: `Standard rate (${fallback}%)`,
  };
}

export function commissionFor(amount: number, percent: number): number {
  return Math.round((Math.max(0, amount) * percent) / 100);
}

/**
 * What to show in the rate box for a bank that has not been given one yet.
 *
 * The bank's own rate when it has one. Otherwise, for the banks whose rate
 * was stated up front, that rate -- so an administrator opening the screen
 * finds it pre-filled rather than having to remember it. It is only a
 * suggestion in the input: nothing is applied until Save is pressed, and any
 * value can be typed over it.
 */
const STATED_BANK_RATES: Record<string, number> = {
  'bank of india': 2,
};

export function suggestedRateFor(bank: Bank): number | undefined {
  if (typeof bank?.defaultCommissionRate === 'number' && bank.defaultCommissionRate > 0) {
    return bank.defaultCommissionRate;
  }
  const byName = STATED_BANK_RATES[(bank?.name || '').trim().toLowerCase()];
  if (typeof byName === 'number') return byName;
  return undefined;
}

/**
 * The three layers on their own: the account's rate, then its bank's, then
 * the system default.
 *
 * This is what a collection resolves to when no hierarchy rule matches, so it
 * is handed to the rules engine as its fallback rate. An explicit rule still
 * wins where one applies; everywhere else these three decide.
 */
export function resolveCommissionRate(
  account: Account | null | undefined,
  banks: Bank[],
  defaultRate: number,
  agent?: User | null,
  sarfaesiRate?: number
): RateResolution {
  return resolveCommissionPercent({
    account,
    bankName: account?.bank,
    banks,
    matchedRulePercent: null,
    matchedRuleName: null,
    agent,
    sarfaesiRate,
    defaultRate,
  });
}
